import { useState } from 'react';
import { Sparkles, X, ExternalLink, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import sprayService, {
  type ChemicalLookupProposal,
  type ChemicalLookupChemicalProposal,
  type ChemicalLookupRegistrationProposal,
  type ChemicalLookupApplyResult,
} from '../../services/spray';
import type { Chemical } from '../../types/spray';
import Button from '../ui/Button';

interface Props {
  chemicalId: number;
  productName: string;
  /**
   * 'create' = the chemicalId points to a fresh stub created via the registry's
   *   "Add via AI lookup" flow. Enables a Stage-2 duplicate check after the
   *   lookup runs: if the proposed APVMA # matches a different existing record,
   *   we hard-stop and offer to apply the lookup to the existing record while
   *   deactivating this stub.
   * 'update' (default) = the chemicalId points to an established record the
   *   user is editing. No duplicate check.
   */
  mode?: 'create' | 'update';
  /**
   * The person holds a Spray Admin seat. Registrations carry the WHP the gate
   * reads, so without one only the product's own details are applied.
   */
  canAddRegistrations?: boolean;
  onClose: () => void;
  onApplied: () => void;
}

type Phase = 'intro' | 'searching' | 'review' | 'applying' | 'done' | 'error' | 'duplicate';

const CHEMICAL_FIELD_LABELS: { key: keyof ChemicalLookupChemicalProposal; label: string; format?: (v: unknown) => string }[] = [
  { key: 'product_name', label: 'Product name' },
  { key: 'trade_name', label: 'Trade name' },
  { key: 'manufacturer', label: 'Manufacturer' },
  { key: 'apvma_approval_number', label: 'APVMA #' },
  { key: 'formulation_type', label: 'Formulation' },
  { key: 'signal_word', label: 'Signal word' },
  { key: 'mode_of_action_group', label: 'MoA group' },
  { key: 'mode_of_action_type', label: 'MoA type' },
  { key: 'sds_url', label: 'SDS URL' },
  { key: 'label_url', label: 'Label URL' },
  {
    key: 'active_ingredients',
    label: 'Active ingredients',
    format: (v) => {
      if (!Array.isArray(v) || v.length === 0) return '';
      return v.map((ai) => {
        const conc = ai.concentration_gl ? `${ai.concentration_gl} g/L` : ai.concentration_pct ? `${ai.concentration_pct}%` : '';
        return `${ai.name}${conc ? ` ${conc}` : ''}${ai.cas_number ? ` (CAS ${ai.cas_number})` : ''}`;
      }).join(', ');
    },
  },
  {
    key: 'ppe_required',
    label: 'PPE required',
    format: (v) => Array.isArray(v) ? v.join(', ') : '',
  },
];

function ConfidenceBadge({ level }: { level: 'HIGH' | 'MEDIUM' }) {
  const cls = level === 'HIGH'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-amber-50 text-amber-700 border-amber-200';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium border ${cls}`}>{level}</span>;
}

function ChemicalLookupModal({ chemicalId, productName, mode: stageMode = 'update', canAddRegistrations = false, onClose, onApplied }: Props) {
  const [phase, setPhase] = useState<Phase>('intro');
  const [proposal, setProposal] = useState<ChemicalLookupProposal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [includeChemical, setIncludeChemical] = useState(true);
  // Map<index, included>
  const [includedRegs, setIncludedRegs] = useState<Set<number>>(new Set());
  const [applyResult, setApplyResult] = useState<ChemicalLookupApplyResult | null>(null);
  const [duplicateMatch, setDuplicateMatch] = useState<Chemical | null>(null);
  const [resolvingDuplicate, setResolvingDuplicate] = useState(false);

  const runLookup = async () => {
    setPhase('searching');
    setError(null);

    const res = await sprayService.lookupChemicalIdentity(chemicalId);
    if (!res.success || !res.data) {
      setError(res.error?.message || 'Lookup failed');
      setPhase('error');
      return;
    }
    const p: ChemicalLookupProposal = res.data.proposal;
    setProposal(p);
    setIncludeChemical(true);
    setIncludedRegs(canAddRegistrations ? new Set((p.registrations || []).map((_, i) => i)) : new Set());

    // Stage 2: in 'create' mode, cross-check the proposed APVMA # against the
    // registry. If another active chemical already has it, abort the apply
    // step and route the user to the existing record.
    if (stageMode === 'create' && p.chemical?.apvma_approval_number) {
      const apvma = p.chemical.apvma_approval_number.trim();
      if (apvma) {
        const dupRes = await sprayService.searchChemicals(apvma, 1, 20);
        if (dupRes.success) {
          const conflict = (dupRes.data || []).find(
            (c) => c.id !== chemicalId
              && c.apvma_approval_number
              && c.apvma_approval_number.trim() === apvma,
          );
          if (conflict) {
            setDuplicateMatch(conflict);
            setPhase('duplicate');
            return;
          }
        }
      }
    }

    setPhase('review');
  };

  const applyToExistingDuplicate = async () => {
    if (!proposal || !duplicateMatch) return;
    setResolvingDuplicate(true);
    setError(null);

    // Apply the proposal to the existing record (every registration, every chemical field).
    const applyPayload = {
      chemical: proposal.chemical,
      registrations: canAddRegistrations ? proposal.registrations : [],
    };
    const applyRes = await sprayService.applyChemicalLookup(duplicateMatch.id, applyPayload);
    if (!applyRes.success) {
      setError(applyRes.error?.message || 'Failed to apply to existing record');
      setResolvingDuplicate(false);
      setPhase('error');
      return;
    }

    // Deactivate the stub we created. Best-effort — don't block on failure.
    await sprayService.updateChemical(chemicalId, { is_active: false });

    setApplyResult(applyRes.data!);
    setResolvingDuplicate(false);
    setPhase('done');
  };

  const discardStubAndClose = async () => {
    setResolvingDuplicate(true);
    await sprayService.updateChemical(chemicalId, { is_active: false });
    setResolvingDuplicate(false);
    onClose();
  };

  const toggleReg = (idx: number) => {
    setIncludedRegs(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const apply = async () => {
    if (!proposal) return;
    setPhase('applying');

    const payload: { chemical?: Partial<ChemicalLookupChemicalProposal>; registrations: ChemicalLookupRegistrationProposal[] } = {
      registrations: proposal.registrations.filter((_, i) => includedRegs.has(i)),
    };
    if (includeChemical) {
      payload.chemical = proposal.chemical;
    }

    const res = await sprayService.applyChemicalLookup(chemicalId, payload);
    if (!res.success) {
      setError(res.error?.message || 'Apply failed');
      setPhase('error');
      return;
    }
    setApplyResult(res.data!);
    setPhase('done');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full my-4">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            <h3 className="text-base font-semibold text-neutral-800">
              APVMA lookup — {productName}
            </h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
          {phase === 'intro' && (
            <div className="space-y-4">
              <p className="text-sm text-neutral-700">
                This looks the product up in the APVMA PUBCRIS register and proposes identity updates
                (manufacturer, formulation, signal word, active constituents, withholding period) for you
                to review. Nothing is saved until you click <strong>Apply</strong>.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
                Deterministic lookup from the official register — no AI, usually a second or two. Per-crop
                application rates are on the registered label and are not in the open dataset; add those
                manually from the label.
              </div>
            </div>
          )}

          {phase === 'searching' && (
            <div className="py-12 text-center">
              <Loader2 className="h-8 w-8 text-purple-600 animate-spin mx-auto mb-3" />
              <p className="text-sm text-neutral-700">Looking up the APVMA register…</p>
            </div>
          )}

          {phase === 'review' && proposal && (
            <div className="space-y-5">
              {proposal.notes_for_user && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
                  {proposal.notes_for_user}
                </div>
              )}

              {/* Chemical metadata */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-neutral-800 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={includeChemical}
                      onChange={(e) => setIncludeChemical(e.target.checked)}
                      className="rounded border-neutral-300"
                    />
                    Chemical metadata
                  </h4>
                  <ConfidenceBadge level={proposal.chemical.confidence} />
                </div>
                <div className={`border border-neutral-200 rounded-lg ${includeChemical ? '' : 'opacity-50'}`}>
                  <table className="w-full text-sm">
                    <tbody>
                      {CHEMICAL_FIELD_LABELS.map(({ key, label, format }) => {
                        const value = proposal.chemical[key];
                        const display = format ? format(value) : (value == null || value === '' ? '' : String(value));
                        if (!display) return null;
                        const isUrl = (key === 'sds_url' || key === 'label_url') && typeof value === 'string';
                        return (
                          <tr key={key} className="border-b border-neutral-100 last:border-0">
                            <td className="px-3 py-2 text-xs font-medium text-neutral-500 w-40">{label}</td>
                            <td className="px-3 py-2 text-neutral-800 break-words">
                              {isUrl ? (
                                <a href={display} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline inline-flex items-center gap-1">
                                  {display.length > 60 ? display.slice(0, 60) + '…' : display}
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              ) : display}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {proposal.chemical.sources.length > 0 && (
                  <div className="mt-2 text-xs text-neutral-500">
                    Sources: {proposal.chemical.sources.map((s, i) => (
                      <a key={i} href={s} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline mr-2 inline-flex items-center gap-1">
                        [{i + 1}] <ExternalLink className="h-3 w-3" />
                      </a>
                    ))}
                  </div>
                )}
              </section>

              {/* Registrations */}
              <section>
                <h4 className="text-sm font-semibold text-neutral-800 mb-2">
                  Crop registrations ({proposal.registrations.length})
                </h4>
                {!canAddRegistrations && proposal.registrations.length > 0 && (
                  <p className="text-xs text-neutral-500 mb-2">
                    Only a Spray Admin can add registrations: their withholding periods feed the check on every spray.
                  </p>
                )}
                {proposal.registrations.length === 0 ? (
                  <p className="text-sm text-neutral-500 italic">
                    Per-crop rates aren't in the APVMA open dataset — add registrations manually from the
                    registered label.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {proposal.registrations.map((reg, i) => {
                      const included = includedRegs.has(i);
                      return (
                        <div key={i} className={`border border-neutral-200 rounded-lg p-3 ${included ? '' : 'opacity-50'}`}>
                          <div className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              checked={included}
                              disabled={!canAddRegistrations}
                              onChange={() => toggleReg(i)}
                              aria-label={`Include ${reg.situation_name} — ${reg.target_pest}`}
                              className="mt-1 rounded border-neutral-300"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <div className="font-medium text-sm text-neutral-800 truncate">
                                  {reg.situation_name} — {reg.target_pest}
                                </div>
                                <ConfidenceBadge level={reg.confidence} />
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-neutral-600">
                                <div><span className="text-neutral-400">Rate:</span> {reg.min_rate_per_ha ?? '?'}{reg.min_rate_per_ha !== reg.max_rate_per_ha ? `–${reg.max_rate_per_ha ?? '?'}` : ''} {reg.rate_unit}</div>
                                {reg.whp_days != null && <div><span className="text-neutral-400">WHP:</span> {reg.whp_days}d</div>}
                                {reg.whp_export_days != null && <div><span className="text-neutral-400">Grazing:</span> {reg.whp_export_days}d</div>}
                                {reg.rei_hours != null && <div><span className="text-neutral-400">REI:</span> {reg.rei_hours}h</div>}
                                {reg.max_applications_per_season != null && <div><span className="text-neutral-400">Max apps:</span> {reg.max_applications_per_season}/season</div>}
                                {reg.min_days_between_apps != null && <div><span className="text-neutral-400">Interval:</span> ≥{reg.min_days_between_apps}d</div>}
                              </div>
                              {reg.growth_stage_restriction && (
                                <div className="text-xs text-neutral-600 mt-1"><span className="text-neutral-400">Growth stage:</span> {reg.growth_stage_restriction}</div>
                              )}
                              {reg.notes && (
                                <div className="text-xs text-neutral-600 mt-1 italic">{reg.notes}</div>
                              )}
                              <div className="mt-1">
                                <a href={reg.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-purple-600 hover:underline inline-flex items-center gap-1">
                                  Source <ExternalLink className="h-3 w-3" />
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* Skipped */}
              {proposal.skipped.length > 0 && (
                <section>
                  <h4 className="text-sm font-semibold text-neutral-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    Skipped ({proposal.skipped.length}) — review manually
                  </h4>
                  <ul className="space-y-1 text-sm text-neutral-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    {proposal.skipped.map((s, i) => (
                      <li key={i}><strong>{s.item}:</strong> {s.reason}</li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}

          {phase === 'applying' && (
            <div className="py-12 text-center">
              <Loader2 className="h-8 w-8 text-purple-600 animate-spin mx-auto mb-3" />
              <p className="text-sm text-neutral-700">Saving…</p>
            </div>
          )}

          {phase === 'done' && applyResult && (
            <div className="space-y-3 py-4">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="h-5 w-5" />
                <span className="text-sm font-medium">Applied successfully</span>
              </div>
              <ul className="text-sm text-neutral-700 list-disc list-inside space-y-1">
                {applyResult.chemicalUpdated && <li>Chemical metadata updated</li>}
                <li>{applyResult.registrationsAdded} registration{applyResult.registrationsAdded === 1 ? '' : 's'} added</li>
                {applyResult.errors.length > 0 && (
                  <li className="text-amber-700">{applyResult.errors.length} item{applyResult.errors.length === 1 ? '' : 's'} skipped or errored — see below</li>
                )}
              </ul>
              {applyResult.errors.length > 0 && (
                <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                  {applyResult.errors.map((e, i) => (
                    <li key={i}>{e.message}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {phase === 'error' && (
            <div className="py-8">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-900">
                <div className="font-semibold mb-1">Lookup failed</div>
                <div>{error}</div>
              </div>
            </div>
          )}

          {phase === 'duplicate' && duplicateMatch && proposal && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 text-sm text-amber-900 flex gap-3">
                <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 text-amber-700" />
                <div className="space-y-2">
                  <div className="font-semibold">Existing chemical with this APVMA # already in registry</div>
                  <div>
                    The lookup returned APVMA # <strong>{proposal.chemical.apvma_approval_number}</strong>, which
                    already belongs to another active record. Creating a second one would be a duplicate.
                  </div>
                </div>
              </div>

              <div className="border border-neutral-200 rounded-lg p-3">
                <div className="text-xs font-medium text-neutral-500 mb-1">Existing record</div>
                <div className="text-sm font-medium text-neutral-800">
                  #{duplicateMatch.id} — {duplicateMatch.product_name}
                  {duplicateMatch.trade_name && duplicateMatch.trade_name !== duplicateMatch.product_name && (
                    <span className="text-neutral-500 font-normal"> · {duplicateMatch.trade_name}</span>
                  )}
                </div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  {[duplicateMatch.manufacturer, `APVMA ${duplicateMatch.apvma_approval_number}`,
                    duplicateMatch.registration_count != null
                      ? `${duplicateMatch.registration_count} existing reg${duplicateMatch.registration_count === 1 ? '' : 's'}`
                      : null,
                  ].filter(Boolean).join(' · ')}
                </div>
              </div>

              <div className="text-sm text-neutral-700 space-y-1">
                <p>You can:</p>
                <ul className="list-disc list-inside text-neutral-600">
                  <li>
                    <strong>Apply the lookup to the existing record</strong> — populates the existing chemical with the
                    research results and deactivates this stub (#{chemicalId}).
                  </li>
                  <li>
                    <strong>Discard</strong> — abandons this stub and closes. The existing record is left untouched
                    and you can edit it manually.
                  </li>
                </ul>
              </div>

              {error && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-between gap-2">
          <div className="text-xs text-neutral-500">
            {phase === 'review' && proposal && (
              <>Selected: {includedRegs.size}/{proposal.registrations.length} registration{proposal.registrations.length === 1 ? '' : 's'}{includeChemical ? ' + chemical fields' : ''}</>
            )}
          </div>
          <div className="flex gap-2">
            {phase === 'intro' && (
              <>
                <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={() => runLookup()}>
                  <Sparkles className="h-4 w-4" /> Look up from APVMA register
                </Button>
              </>
            )}
            {phase === 'review' && (
              <>
                <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={apply}
                  disabled={!includeChemical && includedRegs.size === 0}
                >
                  Apply
                </Button>
              </>
            )}
            {phase === 'done' && (
              <Button variant="primary" size="sm" onClick={() => { onApplied(); onClose(); }}>
                Done
              </Button>
            )}
            {phase === 'error' && (
              <>
                <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
                <Button variant="primary" size="sm" onClick={() => setPhase('intro')}>Try again</Button>
              </>
            )}
            {phase === 'duplicate' && (
              <>
                <Button variant="outline" size="sm" onClick={discardStubAndClose} disabled={resolvingDuplicate}>
                  Discard stub
                </Button>
                <Button variant="primary" size="sm" onClick={applyToExistingDuplicate} disabled={resolvingDuplicate}>
                  {resolvingDuplicate ? 'Applying…' : 'Apply to existing record'}
                </Button>
              </>
            )}
            {(phase === 'searching' || phase === 'applying') && (
              <Button variant="outline" size="sm" disabled>Working…</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChemicalLookupModal;
